import { memo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import { alpha, useTheme } from '@mui/material/styles';

export interface BlockQuoteProps {
  children: ReactNode;
  /** Nesting depth, used to soften the accent on inner quotes. */
  depth?: number;
}

/** Blockquote with a subtle quote glyph; nested quotes lighten progressively. */
export const BlockQuote = memo(function BlockQuote({ children, depth = 0 }: BlockQuoteProps) {
  const theme = useTheme();
  const intensity = Math.max(0.25, 1 - depth * 0.25);

  return (
    <Box
      component="blockquote"
      sx={{
        position: 'relative',
        my: 2.5,
        py: 0.5,
        pl: 2.5,
        pr: 1,
        borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.35 * intensity)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.03 * intensity),
        borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
        color: theme.palette.text.secondary,
        '& > :last-child': { mb: 0 },
      }}
    >
      {depth === 0 && (
        <FormatQuoteRoundedIcon
          aria-hidden
          sx={{
            position: 'absolute',
            top: 4,
            right: 8,
            fontSize: 20,
            opacity: 0.12,
            transform: 'rotate(180deg)',
          }}
        />
      )}
      {children}
    </Box>
  );
});

export default BlockQuote;
